import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const serviceOptions = t("contact.services");
  const [form, setForm] = useState({ name: "", email: "", service: serviceOptions[0], message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", service: serviceOptions[0], message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-white/20 focus:border-[#FF5A00] outline-none py-4 text-neutral-100 placeholder:text-neutral-600 transition-colors duration-300";

  return (
    <section id="contact" className="py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] mb-4" data-testid="contact-eyebrow">
            {t("contact.eyebrow")}
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="contact-heading">
            {t("contact.heading")}
          </h2>
          <p className="text-neutral-400 text-base md:text-lg leading-relaxed mt-6 max-w-md">
            {t("contact.copy")}
          </p>
          <a
            href="mailto:contact@fenrionai.pt"
            className="inline-block mt-10 font-syne text-xl md:text-2xl text-[#FFB800] hover:text-[#FF5A00] transition-colors duration-300"
            data-testid="contact-email-link"
          >
            contact@fenrionai.pt
          </a>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col gap-8"
          data-testid="contact-form"
        >
          <div>
            <label htmlFor="contact-name" className="text-xs uppercase tracking-widest text-neutral-500">
              {t("contact.name")}
            </label>
            <input
              id="contact-name"
              required
              value={form.name}
              onChange={update("name")}
              placeholder={t("contact.namePlaceholder")}
              className={fieldClass}
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="text-xs uppercase tracking-widest text-neutral-500">
              {t("contact.email")}
            </label>
            <input
              id="contact-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              placeholder={t("contact.emailPlaceholder")}
              className={fieldClass}
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label htmlFor="contact-service" className="text-xs uppercase tracking-widest text-neutral-500">
              {t("contact.service")}
            </label>
            <select
              id="contact-service"
              value={form.service}
              onChange={update("service")}
              className={`${fieldClass} bg-[#050505] cursor-pointer`}
              data-testid="contact-service-select"
            >
              {serviceOptions.map((s) => (
                <option key={s} value={s} className="bg-[#0A0A0A]">
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="contact-message" className="text-xs uppercase tracking-widest text-neutral-500">
              {t("contact.details")}
            </label>
            <textarea
              id="contact-message"
              required
              rows={4}
              value={form.message}
              onChange={update("message")}
              placeholder={t("contact.detailsPlaceholder")}
              className={`${fieldClass} resize-none`}
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="group self-start flex items-center gap-2 bg-[#FF5A00] text-black font-medium px-8 py-4 rounded-full hover:bg-[#FFB800] disabled:opacity-60 transition-colors duration-300"
            data-testid="contact-submit-button"
          >
            {sending ? (
              <>
                {t("contact.sending")}
                <Loader2 className="w-5 h-5 animate-spin" />
              </>
            ) : (
              <>
                {t("contact.submit")}
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
